import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { component, ComponentPolicy, ComponentTuple, ObjectManager, property } from '../../../concepts/objects';
import { memoUsing } from '../../../concepts/objects/MemoUsing';
import { Point } from '../../../platform/geom/Point';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { Physical, RelativeTo, TransformHints } from '../../canvas/entities/Physical';
import { RectangularPhysical } from '../../canvas/entities/RectangularPhysical';
import { Snappable } from '../../canvas/entities/Snappable';
import { TextAlignment } from '../../canvas/entities/TextConventions';
import { ToolManager } from '../../canvas/tooling/ToolManager';
import { TextRuler } from '../../canvas/visualization/TextRuler';
import { TextContentField } from './TextContentField';
import { TextLayout } from './TextLayout';
import { textPropertyLayout } from './TextPropertyLayout';

const mutation = defineCommand(
    'textMutation',
    'Text: Mutation',
    (r, [id]: [string]) => {
        r.call(EditorSession).update(AdapterInfo, CreateAdapterInfo(id, 'field'));
    },
    [],
);

const repeat = defineCommand(
    'textRepeat',
    null,
    (r, [id]: [string]) => {
        const objects = r.call(ObjectManager);
        const text = objects.get(id, Text);
        const tools = r.call(ToolManager);
        tools.equip('textTool', {
            color: text.fontColor,
            fontName: text.fontFamily,
        });
    },
);

const textDef = {
    name: 'Text',
    implements: [Physical, Snappable],
    mutation,
    repeat,
    propertyLayout: textPropertyLayout,
};

const textPolicy = ComponentPolicy.create({
    allowAdd: () => [false, 'Text does not support children.']
});

export class TextInfo {
    constructor(public readonly id: string) {
        this.id = id;
    }
}

const layoutAffectingProps = [
    'width', 'height', 'autoHeight',
    'content', 'fontFamily', 'fontSize', 'fontStyle', 'fontWeight', 'fontColor', 'textMargin', 'textAlignH', 'textAlignV',
];

@component(textDef)
export class Text extends RectangularPhysical implements Snappable {

    @property({ type: 'boolean', default: false })
    public autoWidth: boolean;

    @property({ type: 'boolean', default: true })
    public autoHeight: boolean;

    @property({ type: 'string', oldName: 'content|value' })
    public content: string;

    @property({ type: 'string', default: 'Inter', oldName: 'content|fontFamily', style: true })
    public fontFamily!: string;

    @property({ type: 'string', oldName: 'content|fontColor', style: true })
    public fontColor!: string;

    @property({ type: 'number', default: 24, oldName: 'content|fontSize', style: true })
    public fontSize!: number;

    @property({ type: 'string', default: 'normal', oldName: 'content|fontStyle', style: true })
    public fontStyle!: string;

    @property({ type: 'number', default: 400, oldName: 'content|fontWeight', style: true })
    public fontWeight!: number;

    @property({ type: 'number', default: TextAlignment.start, oldName: 'content|textAlignH', style: true })
    public textAlignH!: TextAlignment;

    @property({ type: 'number', default: TextAlignment.start, oldName: 'content|textAlignH', style: true })
    public textAlignV!: TextAlignment;

    public get boundary() {
        return true;
    }

    public get minWidth(): number {
        return 40;
    }

    public get minHeight(): number {
        return 20;
    }

    @memoUsing(...layoutAffectingProps)
    public get layout() {
        return TextLayout(this);
    }

    @memoUsing('layout', 'content')
    public get uiFields() {
        return [TextContentField(this)];
    };

    public get shouldSnap() {
        return true;
    }

    protected get policy() {
        return textPolicy;
    }

    public queryTransformHints() {
        return TransformHints({
            scaleX: true,
            scaleY: !this.autoHeight,
            rotate: true,
            translate: true,
            visible: true,
        });
    }

    public sizeToContent(maxWidth?: number) {

        const { settings } = this.layout.content;

        if (this.autoWidth) {
            const freeSize = TextRuler.measureSize(this.content, settings);
            if (!maxWidth || freeSize.w <= maxWidth) {
                this.width = freeSize.w;
                this.height = freeSize.h;
            }
            else {
                this.autoWidth = false;
                this.width = maxWidth;
                this.sizeToContent();
            }
        }
        else {
            const wrappedHeight = TextRuler.measureHeight(this.content, settings, this.width);
            this.height = wrappedHeight;
        }
    }

    public test(mpt: Point): boolean {
        const { base, content } = this.layout;
        const rpt = this.toRelative(mpt);
        return base.contains(rpt);
    }

    public toEmptyTuple(to?: Physical | RelativeTo): ComponentTuple {
        const ct = super.toShallowTuple(to);
        ct[2].content = '';
        return ct;
    }

    public toHumanString(): string {
        return `Text ("${(this.content).slice(0, 100)}")`;
    }

    public toText(): string {
        return this.content;
    }

    protected setTransformationResult(x: number, y: number, r: number, w: number, h: number): void {

        if (this.width !== w) {
            this.autoWidth = false;
        }

        if (this.autoHeight) {
            const wrappedHeight = TextRuler.measureHeight(this.content, this.layout.content.settings, w);
            h = wrappedHeight;
        }

        super.setTransformationResult(x, y, r, w, h);
    }
}
